import React, {
  useEffect,
  useState,
  useMemo
} from "react";
import { parseInt } from "lodash";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";

import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { Text } from "src/library/components/Text/Text";
import { ButtonLink } from "src/library/components/Link";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import DropInfoSidebar from "src/core/drops/components/DropInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { useDropsApi } from "src/core/drops/api/drops.api";
import { useDrop } from "src/core/drops/hooks";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import PriceListDropdown from "src/core/prices/components/PriceListDropdown";
import ProductApprovalDropdown from "src/modules/product-approval/components/ProductApprovalDropdown";
import { generatePageSizeOptions } from "src/library/components/Tables/utils";
import { DateInput } from "src/core/shared/components/Date/DateInput";
import { Table } from "src/library/components/Tables";
import { productTableColumns } from "src/core/shared/components/tables";
import { ITableInstance } from "src/library/components/Tables/Table";
import {
  IProduct,
  ListProductsFilters,
  useProductsApi
} from "src/core/products/api/products.api";
import { PaginatedParams } from "src/library/utils";
import { IStore } from "src/core/shared/store";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { ProductsTableColumnAcccessors } from "src/core/shared/components/tables/productTableColumns";
import { Input } from "src/library/components/Forms";

interface UrlParams {
  customerId: string;
  dropId: string;
}

const defaultPagination: PaginatedParams<IProduct> = {
  page: 0,
  limit: 12,
  offset: 0,
  sort: [ [ "description", "ASC" ] ]
};

/**
 * Edit Drop contact details page
 */
const DropPriceList: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const { match, terminology } = props;
  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const dropId = match ? parseInt(match.params.dropId, 0) : 0;
  const [ { isLoading: isLoadingDrop, drop }, { checkDropIsSynced } ] = useDrop(dropId);
  const [ updateDropResponse, updateDrop ] = useDropsApi("UPDATE_DROP");
  const [ selectedPriceCode, setSelectedPriceCode ] = useState<number | null>(drop?.priceCodeId || null);
  const [ selectedSpecialPriceCode, setSelectedSpecialPriceCode ] = useState<number | null>(drop?.specialPriceCodeId || null);
  const [ selectedProductApprovalList, setselectedProductApprovalList ] = useState<number | null>(null);
  const [ selectedDate, setSelectedDate ] = useState<Date>(new Date());
  const checkPermission = useCheckUserPermission();
  const TableRef = React.useRef<ITableInstance<IProduct>>(null);
  const [ listProductsResponse, listProducts ] = useProductsApi("LIST_PRODUCTS");
  const productsData = listProductsResponse.data;
  const defaultFilters = {};
  const pricesVisible = usePriceVisibility();

  const { currentRole, installedModules } = useMappedState(({ global }: IStore) => ({
    currentRole: global.role,
    installedModules: global.settings?.installedModules
  }));

  React.useEffect(() => {
    if (drop?.priceCodeId) {
      setSelectedPriceCode(drop?.priceCodeId);
    }
  }, [ drop?.priceCodeId ]);

  React.useEffect(() => {
    if (drop?.specialPriceCodeId) {
      setSelectedSpecialPriceCode(drop?.specialPriceCodeId);
    }
  }, [ drop?.specialPriceCodeId ]);

  React.useEffect(() => {
    if (drop?.productApprovalListId) {
      setselectedProductApprovalList(drop?.productApprovalListId);
    }
  }, [ drop?.productApprovalListId ]);

  const canAmend = useMemo(() => {
    return checkPermission("amend-branch");
  }, [ checkPermission ]);

  // Update the drop state when on updateDropResponse
  useEffect(() => {
    if (updateDropResponse.data) {
      checkDropIsSynced();
      updateDropResponse.data = null;
    }
  }, [ checkDropIsSynced, updateDropResponse ]);

  useEffect(() => {
    TableRef.current?.reloadTable();
  }, [
    selectedPriceCode,
    selectedSpecialPriceCode,
    selectedProductApprovalList,
    selectedDate
  ]);

  const returnActions = (row: any) => {
    const actions = [
      {
        title: `View ${terminology.product.singular.text}`,
        action: () => props.history.push(`/products/${row.original.id}`)
      }
    ];

    return actions;
  };

  const onFetchData = React.useCallback((filters: ListProductsFilters, pagination: PaginatedParams<IProduct>) => {
    if (selectedPriceCode) {
      filters.priceCodeId = selectedPriceCode;
    }

    if (selectedProductApprovalList) {
      filters.productApprovalListId = selectedProductApprovalList;
    }

    if (selectedSpecialPriceCode) {
      filters.specialPriceCodeId = selectedSpecialPriceCode;
    }

    if (filters.priceCodeId) {
      filters.effectiveDate = selectedDate;

      listProducts({
        filters,
        pagination
      });
    }
  }, [
    selectedPriceCode,
    selectedProductApprovalList,
    selectedSpecialPriceCode,
    selectedDate,
    listProducts
  ]);

  return (
    <Layout {...props}>
      <StyleMain fill>
        {isLoadingDrop && (
          <LoadingSpinner
            position='inline'
            type="flat"
          />
        )}

        <div className="form-row-wrapper">
          <PriceListDropdown
            disabled={!canAmend}
            updateSelectedPriceCode={priceCodeId => setSelectedPriceCode(priceCodeId)}
            value={selectedPriceCode}
          />
        </div>

        {(installedModules?.includes("special-price")) && (
          <div className="form-row-wrapper">
            <PriceListDropdown
              disabled={!canAmend}
              label="Special Price List"
              updateSelectedPriceCode={specialPriceCodeId => setSelectedSpecialPriceCode(specialPriceCodeId)}
              value={selectedSpecialPriceCode}
              special
            />
          </div>
        )}

        <div className="form-row-wrapper">
          {installedModules?.includes("product-approval") && (
            <ProductApprovalDropdown
              disabled={!canAmend}
              updateSelectedProductApprovalList={approvalListId => setselectedProductApprovalList(approvalListId)}
              value={selectedProductApprovalList}
            />
          )}
        </div>

        {installedModules?.includes("discount") && (
          <div className="form-row-wrapper">
            <div className="discount-input">
              <Input
                value={drop?.discount}
                name="discount"
                as="input"
                inputType="number"
                disabled
                label="Discount value"
              />
            </div>
          </div>
        )}

        <div className="product-list-wrapper">
          <div className="form-row-wrapper">
            <Text fontSize="h3">
              Products List
            </Text>

            <Text
              as='p'
              fontSize="h4"
            >
              Change the date to see the products that would be available on that delivery date.
            </Text>

            <DateInput
              label="Date Picker"
              date={selectedDate}
              setDate={setSelectedDate}
              minimumDate={new Date()}
            />
          </div>

          <Table
            tableId="global"
            ref={TableRef}
            mainAction={row => props.history.push(`/products/${row.original.id}`)}
            tableOf={terminology.product.plural.text}
            view="list"
            data={productsData ? productsData.result : []}
            error={listProductsResponse.error}
            onFetchData={onFetchData}
            defaultFilters={defaultFilters}
            defaultPagination={defaultPagination}
            pageSizeOptions={generatePageSizeOptions(12)}
            loading={listProductsResponse.pending}
            pages={productsData ? productsData.pages : 0}
            totalRows={productsData ? productsData.count : 0}
            columns={productTableColumns({
              ActionButton: row => (
                <ActionButton
                  title="actions"
                  items={returnActions(row)}
                />
              ),
              terminology: props.terminology,
              currentRole,
              pricesVisible,
              omitColumns: [ ProductsTableColumnAcccessors.LeadTimeGroup, ProductsTableColumnAcccessors.Image ],
              drop,
              installedModules
            })}
          />
        </div>
      </StyleMain>

      <InfoBarWrapper>
        <DropInfoSidebar
          terminology={terminology}
          isLoading={isLoadingDrop}
          data={drop}
          primaryAction={(
            <ButtonLink
              $buttonType="secondary"
              to={`/admin/customers/${customerId}/drops`}
            >
              Cancel
            </ButtonLink>
          )}
          secondaryAction={(
            <Button
              loading={updateDropResponse.pending}
              onClick={() => {
                if (selectedPriceCode && drop) {
                  updateDrop({
                    ...drop,
                    priceCodeId: selectedPriceCode,
                    specialPriceCodeId: selectedSpecialPriceCode,
                    productApprovalListId: selectedProductApprovalList
                  });
                }
              }}
              disabled={!canAmend}
            >
              Save
            </Button>
          )}
        />
      </InfoBarWrapper>
    </Layout>

  );
};

export default DropPriceList;

const StyleMain = styled(MainContentWrapper)`
  ${({ theme }) => css`
    .form-row-wrapper {
      margin-bottom: 1rem
    }
    
    .product-list-wrapper {
      margin-top: ${theme.spacing.margins.lg};
      padding-top: ${theme.spacing.margins.lg};
      border-top: 1px solid ${theme.colors.lightGrey};
    }

    .discount-input {
      margin-top: ${theme.spacing.margins.xs};
      padding-top: ${theme.spacing.margins.xs};
    }
  `}
`;
